import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { conversationApi, memberSummaryApi, messageApi, pickerApi, receiptApi } from '@formosa/shared/api/im';
import imWebSocketClient from '@formosa/shared/api/imWebSocketClient';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { useTravelText } from '../../app/TravelLocaleContext';
import ChatComposer from './ChatComposer';
import ChatMessageRow from './ChatMessageRow';
import ChatIcon from './ChatIcon';
import { chatErrorCopy } from './chatCopy';
import { useAuth } from '../../auth/AuthContext';
import SafeImage from '../../components/SafeImage';
import { editErrorMessage, reactionErrorMessage, revokeErrorMessage, threadErrorMessage } from './travelDirectError';
import {
  CHAT_READ_CONFIRMED,
  chatDate,
  asId,
  conversationIdOf,
  conversationTitle,
  createClientMessageId,
  indexSummaries,
  mergeMessages,
  normalizeConversation,
  normalizeMessage,
  normalizeMessagePage,
} from './travelChatModel';
import {
  applyReactionUpdated,
  reactionMutationKey,
  reactionPickerEmojis,
} from './messageReactionModel';
import MessageReactions from './MessageReactions';
import MessageEditComposer from './MessageEditComposer';
import TravelMessageContent from './renderers/TravelMessageContent';
import {
  canShowMessageEditAction,
  mentionMemberIdsOf,
  trimmedEditText,
} from './messageEditModel';
import { canShowMessageRevokeAction } from './messageRevokeModel';
import { publicJourneyApi } from '../../api/publicJourneyApi';
import {
  RESOLVE_FAILED,
  chunkJourneyPublicationIds,
  collectJourneyPublicationIds,
  failedJourneyPublicationResolve,
  indexJourneyPublicationResolve,
} from './journeyPublicationCardModel';
import './travel-messages.css';
import useConversationTitles from './useConversationTitles';

export default function TravelThreadPage() {
  const { conversationId: routeId } = useParams();
  return <TravelThreadView conversationId={asId(routeId)} />;
}

export function TravelThreadView({
  conversationId,
  backTo = '/messages',
  backLabel,
  heading,
  kicker,
  participantLine,
  readOnly = false,
  readOnlyNotice = '',
}) {
  const { user } = useAuth();
  const { t, intlLocale } = useTravelText();
  const backText = backLabel || t('travel.chat.back');
  const historyRef = useRef(null);
  const nearBottom = useRef(true);
  const myMemberId = asId(user?.memberId || user?.id);
  const generation = useRef(0);
  const pendingClientMessageId = useRef('');
  const readWatermark = useRef({ conversationId, seq: 0 });
  const readSyncBlocked = useRef(false);
  const readInFlight = useRef(null);
  const markLatestRead = useRef(() => {});
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [sendError, setSendError] = useState('');
  const [conversation, setConversation] = useState(null);
  const [summaries, setSummaries] = useState({});
  const groupTitles = useConversationTitles(conversation ? [conversation] : [], myMemberId);
  const [messages, setMessages] = useState([]);
  const latestMessages = useRef(messages);
  latestMessages.current = messages;
  const [hasMoreBefore, setHasMoreBefore] = useState(false);
  const [oldestSeq, setOldestSeq] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [socketStatus, setSocketStatus] = useState(() => imWebSocketClient.getStatus?.() || 'idle');
  const pendingReactions = useRef(new Set());
  const [reactionBusyKey, setReactionBusyKey] = useState('');
  const [reactionError, setReactionError] = useState('');
  const [pickerEmojis, setPickerEmojis] = useState(() => reactionPickerEmojis(null));
  const [editSession, setEditSession] = useState(null);
  const [revokeSession, setRevokeSession] = useState(null);
  const journeyResolveGeneration = useRef(0);
  const requestedJourneyPublicationIds = useRef(new Set());
  const [journeyPublicationResolve, setJourneyPublicationResolve] = useState({});
  const [journeyResolveRetryTick, setJourneyResolveRetryTick] = useState(0);

  useEffect(() => {
    journeyResolveGeneration.current += 1;
    requestedJourneyPublicationIds.current = new Set();
    setJourneyPublicationResolve({});
    return () => { journeyResolveGeneration.current += 1; };
  }, [conversationId, myMemberId]);

  useEffect(() => {
    const pending = collectJourneyPublicationIds(
      messages.filter((message) => message.conversationId === conversationId),
    ).filter((id) => !requestedJourneyPublicationIds.current.has(id));
    if (!pending.length) return;
    pending.forEach((id) => requestedJourneyPublicationIds.current.add(id));
    const requestGeneration = journeyResolveGeneration.current;
    chunkJourneyPublicationIds(pending).forEach((chunk) => {
      publicJourneyApi.resolvePublications(chunk)
        .then((response) => {
          if (requestGeneration !== journeyResolveGeneration.current) return;
          setJourneyPublicationResolve((current) => ({
            ...current,
            ...indexJourneyPublicationResolve(chunk, response),
          }));
        })
        .catch(() => {
          if (requestGeneration !== journeyResolveGeneration.current) return;
          setJourneyPublicationResolve((current) => ({
            ...current,
            ...failedJourneyPublicationResolve(chunk),
          }));
        });
    });
  }, [messages, conversationId, myMemberId, journeyResolveRetryTick]);

  const retryJourneyPublicationResolve = useCallback(() => {
    setJourneyPublicationResolve((current) => {
      const next = {};
      Object.entries(current).forEach(([id, resolution]) => {
        if (resolution?.status === RESOLVE_FAILED) {
          requestedJourneyPublicationIds.current.delete(id);
        } else {
          next[id] = resolution;
        }
      });
      return next;
    });
    setJourneyResolveRetryTick((tick) => tick + 1);
  }, []);

  const load = useCallback(async () => {
    const requestGeneration = ++generation.current;
    if (!/^[1-9]\d*$/.test(conversationId)) {
      setPhase('not-found');
      return;
    }
    setPhase('loading');
    readInFlight.current = null;
    readWatermark.current = { conversationId, memberId: myMemberId, seq: 0 };
    nearBottom.current = true;
    setDraft('');
    pendingClientMessageId.current = '';
    setError('');
    setSendError('');
    setMessages([]);
    try {
      const [detail, page] = await Promise.all([
        conversationApi.getById(conversationId),
        messageApi.list(conversationId, { limit: 50 }),
      ]);
      if (requestGeneration !== generation.current) return;
      if (conversationIdOf(detail) !== conversationId) return;
      const normalized = normalizeConversation(detail);
      const history = normalizeMessagePage(page);
      setConversation(normalized);
      setMessages(history.items);
      setHasMoreBefore(history.hasMoreBefore);
      setOldestSeq(history.oldestSeq);
      setPhase('ready');
      const ids = [...normalized.participantMemberIds, ...history.items.map(message => message.senderMemberId).filter(Boolean)];
      if (normalized.peerMemberId) ids.push(normalized.peerMemberId);
      if (ids.length) {
        try {
          const rows = await memberSummaryApi.batch([...new Set(ids)]);
          if (requestGeneration !== generation.current) return;
          setSummaries(indexSummaries(rows));
        } catch {
          /* fallback title */
        }
      }
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      const notFound = cause?.raw?.key === 'entity.not.found' || cause?.code === 90014;
      if (notFound) setPhase('not-found');
      else {
        setError(threadErrorMessage(cause));
        setPhase('error');
      }
    }
  }, [conversationId, myMemberId]);

  const markVisibleRead = useCallback(() => {
    if (readInFlight.current || readSyncBlocked.current || phase !== 'ready' || !nearBottom.current || document.visibilityState !== 'visible') return;
    const seq = Math.max(0, ...latestMessages.current.filter(row => row.conversationId === conversationId).map(row => Number(row.seq) || 0));
    if (!seq) return;
    if (readWatermark.current.conversationId !== conversationId) readWatermark.current = { conversationId, seq: 0 };
    if (seq <= readWatermark.current.seq) return;
    const previous = readWatermark.current.seq;
    readWatermark.current.seq = seq;
    const requestGeneration = generation.current;
    const request = {};
    readInFlight.current = request;
    receiptApi.markRead(conversationId, seq).then(receipt => {
      if (requestGeneration !== generation.current || asId(receipt?.conversationId) !== conversationId) return;
      window.dispatchEvent(new CustomEvent(CHAT_READ_CONFIRMED, { detail: { ...receipt, memberId: myMemberId } }));
      if (readInFlight.current === request) { readInFlight.current = null; markLatestRead.current(); }
    }).catch(() => {
      if (requestGeneration !== generation.current) return;
      if (readInFlight.current === request) readInFlight.current = null;
      if (readWatermark.current.conversationId === conversationId && readWatermark.current.seq === seq) readWatermark.current.seq = previous;
    });
  }, [conversationId, phase, myMemberId]);
  markLatestRead.current = markVisibleRead;

  useEffect(() => {
    markVisibleRead();
    document.addEventListener('visibilitychange', markVisibleRead);
    return () => document.removeEventListener('visibilitychange', markVisibleRead);
  }, [messages, markVisibleRead]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    readSyncBlocked.current = false;
    let disconnected = false;
    let syncVersion = 0;
    let disconnectBoundary = null;
    let syncing = false;
    let liveEvents = [];
    async function catchUp() {
      const version = ++syncVersion;
      const requestGeneration = generation.current;
      const boundary = disconnectBoundary ?? 0;
      syncing = true;
      liveEvents = [];
      const currentRequest = () => !cancelled && version === syncVersion && requestGeneration === generation.current;
      let beforeSeq;
      let recovered = [];
      try {
        while (currentRequest()) {
          const page = normalizeMessagePage(await messageApi.list(conversationId, { limit: 50, ...(beforeSeq == null ? {} : { beforeSeq }) }));
          if (!currentRequest()) return;
          recovered = mergeMessages(page.items, recovered);
          const oldest = page.oldestSeq ?? page.items[0]?.seq;
          if (!boundary || !page.hasMoreBefore || oldest == null || oldest <= boundary) break;
          if (beforeSeq != null && oldest >= beforeSeq) throw new Error('History cursor did not advance');
          beforeSeq = oldest;
        }
        if (currentRequest()) {
          const events = liveEvents;
          syncing = false;
          disconnectBoundary = null;
          readSyncBlocked.current = false;
          setMessages((current) => events.reduce((rows, event) => (
            event.type === 'MESSAGE_REACTION_UPDATED'
              ? applyReactionUpdated(rows, event.data, myMemberId, conversationId)
              : mergeMessages(rows, [normalizeMessage(event.data)])
          ), mergeMessages(current, recovered)));
        }
      } catch (cause) {
        if (currentRequest()) { syncing = false; setSendError(threadErrorMessage(cause)); }
      }
    }
    const offStatus = imWebSocketClient.onStatus((status) => {
      setSocketStatus(status);
      if (status === 'disconnected' || status === 'reconnecting') {
        disconnected = true;
        readSyncBlocked.current = true;
        if (disconnectBoundary == null) disconnectBoundary = Math.max(0, ...latestMessages.current.map((row) => Number(row.seq) || 0));
        syncVersion += 1;
        syncing = false;
      }
      if (status === 'open' && disconnected) {
        disconnected = false;
        catchUp();
      }
    });
    const offMessage = imWebSocketClient.subscribe((envelope) => {
      const type = envelope?.type;
      if (!envelope.data) return;
      if (syncing && ['MESSAGE_CREATED', 'MESSAGE_EDITED', 'MESSAGE_REVOKED', 'MESSAGE_REACTION_UPDATED'].includes(type) && asId(envelope.data.conversationId) === conversationId) liveEvents.push(envelope);
      if (type === 'MESSAGE_REACTION_UPDATED') {
        const eventConversationId = asId(envelope.data.conversationId);
        if (eventConversationId && eventConversationId !== conversationId) return;
        setMessages((current) => applyReactionUpdated(current, envelope.data, myMemberId, conversationId));
        return;
      }
      if (type !== 'MESSAGE_CREATED' && type !== 'MESSAGE_EDITED' && type !== 'MESSAGE_REVOKED') return;
      const incoming = normalizeMessage(envelope.data);
      if (incoming.conversationId !== conversationId) return;
      setMessages((current) => mergeMessages(current, [incoming]));
      if (type === 'MESSAGE_REVOKED' && incoming.serverMessageId) {
        setEditSession((session) => (
          session && String(session.serverMessageId) === String(incoming.serverMessageId) ? null : session
        ));
        setRevokeSession((session) => (
          session && String(session.serverMessageId) === String(incoming.serverMessageId) ? null : session
        ));
      }
    });
    return () => {
      cancelled = true;
      syncVersion += 1;
      if (typeof offStatus === 'function') offStatus();
      if (typeof offMessage === 'function') offMessage();
    };
  }, [conversationId, myMemberId]);

  useEffect(() => {
    let cancelled = false;
    const request = pickerApi.reactions?.();
    if (!request) return undefined;
    Promise.resolve(request)
      .then((data) => {
        if (!cancelled) setPickerEmojis(reactionPickerEmojis(data));
      })
      .catch(() => {
        if (!cancelled) setPickerEmojis(reactionPickerEmojis(null));
      });
    return () => { cancelled = true; };
  }, [conversationId]);

  async function loadOlder() {
    if (!hasMoreBefore || loadingOlder || oldestSeq == null) return;
    const requestGeneration = generation.current;
    const node = historyRef.current;
    const previousHeight = node?.scrollHeight || 0;
    const previousTop = node?.scrollTop || 0;
    nearBottom.current = false;
    setLoadingOlder(true);
    try {
      const page = normalizeMessagePage(await messageApi.list(conversationId, { beforeSeq: oldestSeq, limit: 50 }));
      if (requestGeneration !== generation.current) return;
      setMessages((current) => mergeMessages(page.items, current));
      requestAnimationFrame(() => { if (historyRef.current === node && node) node.scrollTop = previousTop + node.scrollHeight - previousHeight; });
      setHasMoreBefore(page.hasMoreBefore);
      setOldestSeq(page.oldestSeq ?? oldestSeq);
    } finally {
      if (requestGeneration === generation.current) setLoadingOlder(false);
    }
  }

  async function send() {
    const text = draft.trim();
    if (!text || sending) return;
    if (!pendingClientMessageId.current) pendingClientMessageId.current = createClientMessageId();
    const clientMessageId = pendingClientMessageId.current;
    const requestGeneration = generation.current;
    setSending(true);
    setSendError('');
    try {
      const saved = await messageApi.sendText(conversationId, { clientMessageId, text });
      if (requestGeneration !== generation.current) return;
      pendingClientMessageId.current = '';
      setDraft('');
      nearBottom.current = true;
      setMessages((current) => mergeMessages(current, [saved]));
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setSendError(threadErrorMessage(cause));
    } finally {
      if (requestGeneration === generation.current) setSending(false);
    }
  }

  function conversationWriteBlocked() {
    return readOnly
      || String(conversation?.status || '').toUpperCase() === 'READONLY'
      || String(conversation?.status || '').toUpperCase() === 'CLOSED';
  }

  function clearEdit() {
    setEditSession(null);
  }

  function startEdit(message) {
    if (!canShowMessageEditAction({
      message,
      currentMemberId: myMemberId,
      writable: !conversationWriteBlocked(),
    })) return;
    setEditSession({
      serverMessageId: String(message.serverMessageId),
      draft: message.text || '',
      error: '',
      saving: false,
    });
    setRevokeSession(null);
  }

  async function saveEdit() {
    const text = trimmedEditText(editSession?.draft);
    const serverMessageId = editSession?.serverMessageId ? String(editSession.serverMessageId) : '';
    if (!text || editSession?.saving || !serverMessageId) return;
    if (conversationWriteBlocked()) {
      setEditSession((current) => (current ? {
        ...current,
        error: editErrorMessage({ code: 91149, raw: { key: 'im.conversation.not_writable' } }),
      } : current));
      return;
    }
    const requestGeneration = generation.current;
    const current = messages.find((row) => String(row.serverMessageId) === serverMessageId);
    setEditSession((session) => (session ? { ...session, saving: true, error: '' } : session));
    try {
      const saved = await messageApi.editText(
        conversationId,
        serverMessageId,
        text,
        mentionMemberIdsOf(current),
      );
      if (requestGeneration !== generation.current) return;
      setMessages((rows) => mergeMessages(rows, [saved]));
      setEditSession(null);
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setEditSession((session) => (session ? {
        ...session,
        saving: false,
        error: editErrorMessage(cause),
      } : session));
    }
  }

  function startRevoke(message) {
    if (editSession && String(editSession.serverMessageId) === String(message.serverMessageId)) return;
    if (!canShowMessageRevokeAction({
      message,
      currentMemberId: myMemberId,
      writable: !conversationWriteBlocked(),
    })) return;
    setRevokeSession({
      serverMessageId: String(message.serverMessageId),
      saving: false,
      error: '',
    });
  }

  function cancelRevoke() {
    setRevokeSession(null);
  }

  async function confirmRevoke() {
    const serverMessageId = revokeSession?.serverMessageId ? String(revokeSession.serverMessageId) : '';
    if (!serverMessageId || revokeSession?.saving) return;
    if (conversationWriteBlocked()) {
      setRevokeSession((current) => (current ? {
        ...current,
        error: revokeErrorMessage({ code: 91149, raw: { key: 'im.conversation.not_writable' } }),
      } : current));
      return;
    }
    const requestGeneration = generation.current;
    setRevokeSession((session) => (session ? { ...session, saving: true, error: '' } : session));
    try {
      const saved = await messageApi.revoke(conversationId, serverMessageId);
      if (requestGeneration !== generation.current) return;
      setMessages((rows) => mergeMessages(rows, [saved]));
      setRevokeSession(null);
      setEditSession((session) => (
        session && String(session.serverMessageId) === serverMessageId ? null : session
      ));
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setRevokeSession((session) => (session ? {
        ...session,
        saving: false,
        error: revokeErrorMessage(cause),
      } : session));
    }
  }

  async function toggleReaction(serverMessageId, emoji) {
    const writeBlockedNow = readOnly
      || String(conversation?.status || '').toUpperCase() === 'READONLY'
      || String(conversation?.status || '').toUpperCase() === 'CLOSED';
    if (writeBlockedNow || !serverMessageId || !emoji) return;
    const key = reactionMutationKey(serverMessageId, emoji);
    if (pendingReactions.current.has(key)) return;
    pendingReactions.current.add(key);
    setReactionBusyKey(key);
    setReactionError('');
    const requestGeneration = generation.current;
    try {
      const saved = await messageApi.putReaction(conversationId, serverMessageId, emoji);
      if (requestGeneration !== generation.current) return;
      setMessages((current) => mergeMessages(current, [saved]));
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setReactionError(reactionErrorMessage(cause));
    } finally {
      pendingReactions.current.delete(key);
      if (requestGeneration === generation.current) {
        setReactionBusyKey((current) => (current === key ? '' : current));
      }
    }
  }

  useEffect(() => {
    const node = historyRef.current;
    if (node && nearBottom.current) node.scrollTop = node.scrollHeight;
  }, [messages, phase]);
  useEffect(() => {
    const node = historyRef.current;
    if (phase !== 'ready' || !node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => { if (nearBottom.current) node.scrollTop = node.scrollHeight; });
    observer.observe(node.querySelector('ol') || node);
    return () => observer.disconnect();
  }, [phase]);


  if (phase === 'loading') {
    return (
      <main className="travel-messages-state" aria-live="polite">
        <div className="explore-spinner" />
        <p>{t('travel.chat.loading')}</p>
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="travel-messages-state">
        <h1>{t('travel.chat.notFound')}</h1>
        <p>{t('travel.chat.unavailable')}</p>
        <Link to={backTo} aria-label={backText}><ChatIcon name="back" /><span>{backText}</span></Link>
      </main>
    );
  }
  if (phase === 'error') {
    return (
      <main className="travel-messages-state" role="alert">
        <h1>{t('travel.chat.loadFailed')}</h1>
        <p>{chatErrorCopy(error, t)}</p>
        <button type="button" onClick={load}>{t('travel.chat.retry')}</button>
      </main>
    );
  }

  const title = conversationTitle(conversation, summaries, groupTitles);
  const peer = summaries[conversation.peerMemberId];
  const avatar = resolveMemberMediaUrl(peer?.avatarUrl || conversation.avatarUrl);
  const degraded = socketStatus === 'disconnected' || socketStatus === 'reconnecting';
  const writeBlocked = readOnly || String(conversation?.status || '').toUpperCase() === 'READONLY'
    || String(conversation?.status || '').toUpperCase() === 'CLOSED';
  const writeNotice = readOnlyNotice || (writeBlocked ? t('travel.chat.readonly') : '');

  return (
    <main className="travel-thread">
      <header className="travel-thread__header">
        <Link to={backTo} aria-label={backText}><ChatIcon name="back" /><span>{backText}</span></Link>
        <SafeImage
          className="travel-thread__avatar"
          src={avatar}
          alt=""
          fallback={<span className="travel-thread__avatar" aria-hidden="true">{title.slice(0, 1).toUpperCase()}</span>}
        />
        <div>
          {kicker ? <p className="travel-thread__kicker">{kicker}</p> : null}
          <h1>{heading || title}</h1>
          {participantLine ? <p className="travel-thread__participants">{participantLine}</p> : null}
          {degraded ? <p className="travel-thread__status">{t('travel.chat.degraded')}</p> : null}
        </div>
      </header>
      <section ref={historyRef} onScroll={event => { const n = event.currentTarget; nearBottom.current = n.scrollHeight - n.clientHeight - n.scrollTop < 80; markVisibleRead(); }} className="travel-thread__history" aria-live="polite">
        {hasMoreBefore ? (
          <button type="button" className="travel-thread__older" disabled={loadingOlder} onClick={loadOlder}>
            {t(loadingOlder ? 'travel.chat.busy' : 'travel.chat.older')}
          </button>
        ) : null}
        {messages.length === 0 ? <p className="travel-thread__empty">{t('travel.chat.empty')}</p> : null}
        <ol className="travel-thread__list">
          {messages.map((message, index) => {
            const date = chatDate(message.createdAt, intlLocale);
            const previousDate = index ? chatDate(messages[index - 1].createdAt, intlLocale) : null;
            const mine = message.senderMemberId === myMemberId;
            const editing = String(editSession?.serverMessageId || '') === String(message.serverMessageId);
            const revoking = String(revokeSession?.serverMessageId || '') === String(message.serverMessageId);
            const showEdit = canShowMessageEditAction({
              message,
              currentMemberId: myMemberId,
              writable: !writeBlocked,
            });
            const showRevoke = canShowMessageRevokeAction({
              message,
              currentMemberId: myMemberId,
              writable: !writeBlocked,
            });
            return (
              <Fragment key={message.serverMessageId || message.clientMessageId}>
                {date && date.key !== previousDate?.key ? <li className="travel-thread__date"><time dateTime={message.createdAt}>{date.label}</time></li> : null}
              <ChatMessageRow
                mine={mine} group={['GROUP', 'BUSINESS_GROUP'].includes(conversation.conversationType)} sender={summaries[message.senderMemberId]}
              >
                <div className="travel-chat-message-body">{editing ? (
                  <MessageEditComposer
                    value={editSession.draft}
                    onChange={(draft) => setEditSession((session) => (session ? { ...session, draft } : session))}
                    onSave={saveEdit}
                    onCancel={clearEdit}
                    saving={Boolean(editSession.saving)}
                    error={chatErrorCopy(editSession.error, t)}
                    disabled={writeBlocked}
                  />
                ) : (
                  <TravelMessageContent
                    message={message}
                    journeyPublicationResolution={message.journeyPublicationId
                      ? journeyPublicationResolve[message.journeyPublicationId]
                      : undefined}
                    onRetryJourneyPublication={retryJourneyPublicationResolve}
                  />
                )}
                </div>
                <MessageReactions
                  message={message}
                  canMutate={!writeBlocked}
                  busyEmoji={reactionBusyKey.startsWith(`${message.serverMessageId}:`)
                    ? reactionBusyKey.slice(message.serverMessageId.length + 1)
                    : ''}
                  pickerEmojis={pickerEmojis}
                  onToggle={(emoji) => toggleReaction(message.serverMessageId, emoji)}
                />
                {revoking ? (
                  <div className="travel-msg-revoke" role="group" aria-label={t('travel.chat.confirmRevoke')}>
                    <p>{t('travel.chat.revokePrompt')}</p>
                    <div className="travel-msg-revoke__actions">
                      <button type="button" onClick={cancelRevoke} disabled={Boolean(revokeSession.saving)}>{t('travel.chat.cancel')}</button>
                      <button
                        type="button"
                        onClick={confirmRevoke}
                        disabled={Boolean(revokeSession.saving) || writeBlocked}
                        aria-busy={Boolean(revokeSession.saving)}
                      >
                        {t(revokeSession.saving ? 'travel.chat.revoking' : 'travel.chat.confirmRevoke')}
                      </button>
                    </div>
                    {revokeSession.error ? (
                      <p className="travel-thread__send-error" role="alert" aria-live="assertive">{chatErrorCopy(revokeSession.error, t)}</p>
                    ) : null}
                  </div>
                ) : null}
                <div className="travel-thread__bubble-foot">
                  <time dateTime={message.createdAt || undefined}>{message.createdAt && !Number.isNaN(new Date(message.createdAt).getTime()) ? new Intl.DateTimeFormat(intlLocale, { hour: '2-digit', minute: '2-digit' }).format(new Date(message.createdAt)) : ''}</time>
                  {message.editedAt && message.status !== 'REVOKED' && !message.revokedAt ? (
                    <span className="travel-thread__edited">{t('travel.chat.edited')}</span>
                  ) : null}
                  {(showEdit || showRevoke) && !editing && !revoking ? <details className="travel-chat-message-more"><summary aria-label={t('travel.chat.more')}><ChatIcon name="more" /></summary><div className="travel-chat-message-menu">
                  {showEdit && !editing && !revoking ? (
                    <button
                      type="button"
                      className="travel-thread__edit"
                      aria-label={t('travel.chat.editMessage')}
                      onClick={() => startEdit(message)}
                    >
                      編輯
                    </button>
                  ) : null}
                  {showRevoke && !editing && !revoking ? (
                    <button
                      type="button"
                      className="travel-thread__revoke"
                      aria-label={t('travel.chat.revokeMessage')}
                      onClick={() => startRevoke(message)}
                    >
                      撤回
                    </button>
                  ) : null}
                  </div></details> : null}
                </div>
              </ChatMessageRow>
              </Fragment>
            );
          })}
        </ol>
      </section>
      {writeBlocked ? (
        <div className="travel-thread__readonly-wrap">
          <p className="travel-thread__readonly" role="status">{writeNotice}</p>
          {reactionError ? <p className="travel-thread__send-error" role="alert">{chatErrorCopy(reactionError, t)}</p> : null}
        </div>
      ) : (
        <ChatComposer key={conversationId} conversationId={conversationId} draft={draft} setDraft={setDraft} sending={sending} onSend={send} onSent={saved => { nearBottom.current = true; setMessages(current => mergeMessages(current, [saved])); }} error={chatErrorCopy(sendError || reactionError, t)} />
      )}
    </main>
  );
}
